'use client';
import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { forwardRef, useState, useRef, useEffect } from 'react';
import { Paragraph } from '../paragraph/paragraph.js';
import { ValidationMessage } from '../validation-message/validation-message.js';
import { isInputLike } from './field-observer.js';

const label = (text, count) => text.replace('%d', Math.abs(count).toString());
/**
 * FieldCounter component, used to display a counter for a form field.
 *
 * @example
 * <Field>
 *   <Input />
 *   <Field.Counter limit={100} under='%d tegn igjen' over='%d tegn for mye' />
 * </Field>
 */
const FieldCounter = forwardRef(function FieldCounter({ limit, under = '%d tegn igjen', over = '%d tegn for mye', ...rest }, ref) {
    const [count, setCount] = useState(0);
    const fieldInputRef = useRef(null);
    const counterRef = useRef(null);
    const hasExceededLimit = count > limit;
    const remainder = limit - count;
    // Listen to native input events (user typing) to update the counter in real time
    useEffect(() => {
        const field = counterRef.current?.closest('.ds-field');
        const input = Array.from(field?.getElementsByTagName('*') || []).find(isInputLike);
        const onInput = ({ target }) => {
            if (isInputLike(target))
                setCount(target.value.length);
        };
        if (input)
            onInput({ target: input }); // Initial setup
        fieldInputRef.current = input;
        field?.addEventListener('input', onInput);
        return () => field?.removeEventListener('input', onInput);
    }, []);
    /* React does not dispatch a native input event when the value prop changes externally.
    Since the parent re-renders this component when value changes, we can sync on render. */
    useEffect(() => {
        if (fieldInputRef.current) {
            const valueLength = fieldInputRef.current.value.length;
            setCount((prev) => (prev === valueLength ? prev : valueLength));
        }
    });
    return (jsxs(Fragment, { children: [jsx("div", { "data-field": 'description', className: 'ds-sr-only', "aria-live": 'polite', ref: counterRef, children: hasExceededLimit && label(over, remainder) }), hasExceededLimit ? (jsx(ValidationMessage, { ref: ref, ...rest, children: label(over, remainder) })) : (jsx(Paragraph, { ref: ref, ...rest, "data-field": 'validation', children: label(under, remainder) }))] }));
});

export { FieldCounter };
